import { Link, useParams } from "react-router-dom";
import AtriumIcon from "@/components/atrium/AtriumIcon";
import AtriumKpiTile from "@/components/atrium/AtriumKpiTile";
import AtriumPanel from "@/components/atrium/AtriumPanel";
import { CLIENT_META, type ClientSlug } from "@/components/atrium/atriumData";
import { useTasks } from "@/hooks/useTasks";
import { useActionEmails } from "@/hooks/useActionEmails";
import { useAlerts } from "@/hooks/useAlerts";
import { useClientSnapshot } from "@/hooks/useClientSnapshot";
import { useKpi } from "@/hooks/useKpi";
import { useVaultArticles } from "@/hooks/useVaultArticles";
import { agoLabel } from "@/lib/format";

function DisabledBlock({ label }: { label: string }) {
  return (
    <div className="px-[14px] py-3 text-[12px]" style={{ color: "var(--a-muted)" }}>
      {label} is not connected in the cockpit yet.
    </div>
  );
}

export default function AtriumClientDetail() {
  const { slug } = useParams<{ slug: ClientSlug }>();
  if (!slug || !(slug in CLIENT_META)) {
    return (
      <div className="p-5">
        <Link to="/clients" className="a-link text-[12px]" style={{ color: "var(--a-muted)" }}>
          Back to clients
        </Link>
        <div className="mt-4 text-[14px]">Unknown client.</div>
      </div>
    );
  }
  return <ClientDetailContent slug={slug} />;
}

function ClientDetailContent({ slug }: { slug: ClientSlug }) {
  const c = CLIENT_META[slug];
  const tasks = useTasks({ client: slug });
  const taskRows = tasks.data?.rows ?? [];
  const overdue = taskRows.filter((r) => r.bucket === "overdue").length;
  const emails = useActionEmails();
  const clientEmails = emails.data?.rows.filter((e) => {
    const haystack = `${e.sender} ${e.subject}`.toLowerCase();
    return haystack.includes(c.name.toLowerCase()) || haystack.includes(slug);
  }) ?? [];
  const alerts = useAlerts();
  const clientAlerts = alerts.data?.rows.filter((a) => a.client === slug) ?? [];
  const snap = useClientSnapshot(slug);
  const kpi = useKpi();
  const kpiRow = kpi.data?.find((row) => row.client === slug);
  const meetings = useVaultArticles({ client: slug, kind: "meeting", limit: 5 });
  const updates = useVaultArticles({ client: slug, sinceDays: 14, limit: 8 });

  const statusColor = clientAlerts.length || overdue ? "var(--a-bad)" : kpiRow ? "var(--a-ok)" : "var(--a-warn)";
  const statusLabel = clientAlerts.length || overdue ? "Needs attention" : kpiRow ? "OK" : "Watch";

  return (
    <div className="p-5 flex flex-col gap-4">
      <div className="flex items-center gap-3.5">
        <Link to="/clients" className="text-[12px] px-2.5 py-[5px] rounded-[7px] border a-link" style={{ borderColor: "var(--a-border)", color: "var(--a-muted)" }}>
          Back
        </Link>
        <div className="w-9 h-9 rounded-[9px] flex items-center justify-center text-[12px] font-bold tracking-[0.5px] border" style={{ background: "var(--a-panel)", borderColor: "var(--a-border)" }}>
          {c.ticker}
        </div>
        <div>
          <div className="text-[22px] font-semibold tracking-[-0.015em]">{c.name}</div>
          <div className="text-[12px]" style={{ color: "var(--a-muted)" }}>
            {c.serviceScope} - {c.services.join(" / ")}
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="a-dot" style={{ background: statusColor }} />
          <span className="text-[12px]" style={{ color: "var(--a-muted)" }}>{statusLabel}</span>
        </div>
      </div>

      <div className="flex gap-3">
        <AtriumKpiTile label="Open tasks" value={snap.data?.open_tasks ?? "-"} sub={`${overdue} overdue`} color={overdue ? "var(--a-bad)" : undefined} />
        <AtriumKpiTile label="Active alerts" value={clientAlerts.length} color={clientAlerts.length ? "var(--a-bad)" : undefined} />
        <AtriumKpiTile label="Recent updates" value={snap.data?.updates_7d ?? "-"} sub="last 7d" />
        <AtriumKpiTile label="KPI cache" value={kpiRow ? "connected" : "not connected"} color={kpiRow ? "var(--a-ok)" : "var(--a-warn)"} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <AtriumKpiTile label="ROAS" value={kpiRow?.roas != null ? kpiRow.roas.toFixed(2) : "not connected"} sub={kpiRow?.cpm != null ? `CPM EUR ${kpiRow.cpm.toFixed(2)}` : undefined} />
        <AtriumKpiTile label="Spend pace" value={kpiRow?.spend_pace != null ? `${Math.round(kpiRow.spend_pace * 100)}%` : "not connected"} />
        <AtriumKpiTile label="Email" value={kpiRow?.open_rate != null ? `${Math.round(kpiRow.open_rate * 100)}% open` : "not connected"} sub={kpiRow?.revenue_per_recipient != null ? `RPR EUR ${kpiRow.revenue_per_recipient.toFixed(2)}` : undefined} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <AtriumPanel title={`Tasks - ${taskRows.length}`}>
          {taskRows.length === 0 && <div className="px-[14px] py-2.5 text-[12px]" style={{ color: "var(--a-muted)" }}>None.</div>}
          {taskRows.slice(0, 8).map((t) => (
            <div key={t.task_id} className="flex items-center gap-2.5 px-[14px] py-2 text-[13px]">
              <div className="w-[14px] h-[14px] rounded border-[1.5px]" style={{ borderColor: "var(--a-border)" }} />
              <span className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap">{t.name}</span>
              <span className="mono text-[11px]" style={{ color: t.bucket === "overdue" ? "var(--a-bad)" : "var(--a-muted)" }}>
                {t.due_date ? agoLabel(t.due_date) : t.bucket}
              </span>
            </div>
          ))}
        </AtriumPanel>

        <AtriumPanel title={`Inbox - ${clientEmails.length}`}>
          {clientEmails.length === 0 && <div className="px-[14px] py-2.5 text-[12px]" style={{ color: "var(--a-muted)" }}>No matching cockpit emails.</div>}
          {clientEmails.slice(0, 6).map((m) => (
            <div key={m.message_id} className="flex items-center gap-2.5 px-[14px] py-2 text-[13px]">
              <AtriumIcon name="mail" size={13} />
              <span className="w-[140px] truncate" style={{ color: "var(--a-muted)" }}>{m.sender}</span>
              <span className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap">{m.subject}</span>
              <span className="mono text-[11px]" style={{ color: "var(--a-faint)" }}>{agoLabel(m.received_at)}</span>
            </div>
          ))}
        </AtriumPanel>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <AtriumPanel title="Alerts">
          {clientAlerts.length === 0 && <div className="px-[14px] py-2.5 text-[12px]" style={{ color: "var(--a-muted)" }}>No alerts.</div>}
          {clientAlerts.slice(0, 6).map((a) => (
            <div key={a.id} className="flex items-start gap-2.5 px-[14px] py-2.5 text-[13px]">
              <span className="a-dot mt-1.5" style={{ background: a.severity === "critical" ? "var(--a-bad)" : "var(--a-warn)" }} />
              <div className="flex-1">
                <div>{a.title}</div>
                <div className="text-[11px] mt-0.5" style={{ color: "var(--a-muted)" }}>{agoLabel(a.created_at)}</div>
              </div>
            </div>
          ))}
        </AtriumPanel>

        <AtriumPanel title="Recent meetings">
          {(meetings.data ?? []).length === 0 && <DisabledBlock label="Meeting cache" />}
          {(meetings.data ?? []).map((m) => (
            <div key={m.slug} className="flex items-center gap-2.5 px-[14px] py-2.5 text-[13px]">
              <AtriumIcon name="cal" size={13} />
              <span className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap">{m.title}</span>
              <span className="mono text-[11px]" style={{ color: "var(--a-faint)" }}>{agoLabel(m.updated_at)}</span>
            </div>
          ))}
        </AtriumPanel>
      </div>

      <AtriumPanel title="Recent vault updates">
        {(updates.data ?? []).length === 0 && <DisabledBlock label="Vault update cache" />}
        {(updates.data ?? []).map((u) => (
          <div key={u.slug} className="flex items-center gap-2.5 px-[14px] py-2.5 text-[13px]">
            <AtriumIcon name="book" size={13} />
            <span className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap">{u.title}</span>
            <span className="a-chip">{u.kind}</span>
            <span className="mono text-[11px]" style={{ color: "var(--a-faint)" }}>{agoLabel(u.updated_at)}</span>
          </div>
        ))}
      </AtriumPanel>
    </div>
  );
}
